import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { liberacaoService } from '../services';
import { 
  ArrowLeft, 
  FileText,
  User,
  Calendar,
  Package,
  AlertTriangle,
  Download,
  CheckCircle,
  XCircle,
  Clock
} from 'lucide-react';

const DetalhesLiberacao = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [liberacao, setLiberacao] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });

  const carregarLiberacao = useCallback(async () => {
    try {
      setLoading(true);
      const data = await liberacaoService.getById(id);
      setLiberacao(data);
    } catch (error) {
      console.error('Erro ao carregar liberação:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar dados da liberação' });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    carregarLiberacao();
  }, [carregarLiberacao]);

  const formatarData = (data) => {
    if (!data) return '-';
    return new Date(data).toLocaleDateString('pt-BR');
  };

  const formatarDataHora = (data) => {
    if (!data) return '-';
    return new Date(data).toLocaleString('pt-BR');
  };

  const formatarCpf = (cpf) => {
    if (!cpf) return '-';
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const formatarCartaoSus = (cartao) => {
    if (!cartao) return '-';
    return cartao.replace(/(\d{3})(\d{4})(\d{4})(\d{4})/, '$1 $2 $3 $4');
  };

  const getStatusInfo = (status) => {
    const statusConfig = {
      pendente: { 
        icon: Clock, 
        color: '#ffc107', 
        text: 'Pendente',
        description: 'Aguardando confirmação'
      },
      confirmada: { 
        icon: CheckCircle, 
        color: '#28a745', 
        text: 'Confirmada',
        description: 'Liberação realizada com sucesso'
      },
      cancelada: { 
        icon: XCircle, 
        color: '#dc3545', 
        text: 'Cancelada',
        description: 'Liberação foi cancelada'
      }
    };

    return statusConfig[status] || { 
      icon: AlertTriangle, 
      color: '#6c757d', 
      text: status,
      description: ''
    };
  };

  const handleCancelar = async () => {
    if (!window.confirm('Tem certeza que deseja cancelar esta liberação?')) {
      return;
    }

    try {
      await liberacaoService.cancelar(id);
      setMessage({ type: 'success', text: 'Liberação cancelada com sucesso' });
      carregarLiberacao(); // Recarregar dados
    } catch (error) {
      console.error('Erro ao cancelar liberação:', error);
      setMessage({ type: 'error', text: 'Erro ao cancelar liberação' });
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!liberacao) {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
          <AlertTriangle size={48} color="#ff6b6b" style={{ marginBottom: '15px' }} />
          <h3>Liberação não encontrada</h3>
          <p style={{ color: '#667', marginBottom: '20px' }}>
            A liberação solicitada não existe ou foi removida.
          </p>
          <button 
            onClick={() => navigate('/liberacoes')}
            className="btn btn-primary"
          >
            <ArrowLeft size={20} />
            Voltar para Liberações
          </button>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusInfo(liberacao.status);
  const StatusIcon = statusInfo.icon;

  return (
    <div className="container">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '30px',
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        <div>
          <h1 style={{ color: '#333', margin: '0', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FileText size={32} />
            Detalhes da Liberação
          </h1>
          <p style={{ color: '#667', margin: '5px 0 0 0' }}>
            Recibo nº {liberacao.numeroRecibo}
          </p>
        </div>
        
        <div style={{ display: 'flex', gap: '10px' }}>
          {liberacao.status === 'confirmada' && (
            <button 
              onClick={() => navigate(`/liberacoes/recibo/${liberacao.id}`)}
              className="btn btn-primary"
            >
              <Download size={20} />
              Baixar Recibo
            </button>
          )}
          {liberacao.status === 'pendente' && user.cargo === 'admin' && (
            <button 
              onClick={handleCancelar}
              className="btn btn-danger"
            >
              <XCircle size={20} />
              Cancelar
            </button>
          )}
          <button 
            onClick={() => navigate('/liberacoes')}
            className="btn btn-secondary"
          >
            <ArrowLeft size={20} />
            Voltar
          </button>
        </div>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type}`} style={{ marginBottom: '20px' }}>
          {message.type === 'error' && <AlertTriangle size={20} />}
          {message.text}
        </div>
      )}

      {/* Status da Liberação */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '15px',
          padding: '20px',
          backgroundColor: statusInfo.color + '20',
          borderRadius: '8px',
          border: `2px solid ${statusInfo.color}`
        }}>
          <StatusIcon size={48} color={statusInfo.color} />
          <div>
            <h2 style={{ margin: '0', color: statusInfo.color }}>
              {statusInfo.text}
            </h2>
            <p style={{ margin: '5px 0 0 0', color: '#667' }}>
              {statusInfo.description}
            </p>
          </div>
        </div>
      </div>

      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '20px',
        marginBottom: '20px'
      }}>
        {/* Informações da Liberação */}
        <div className="card">
          <h3 style={{ 
            color: '#495057', 
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <FileText size={20} />
            Informações da Liberação
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div className="info-item">
              <label>Número do Recibo:</label>
              <span style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                {liberacao.numeroRecibo}
              </span>
            </div>
            
            <div className="info-item">
              <label>Data da Liberação:</label>
              <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <Calendar size={16} />
                {formatarDataHora(liberacao.dataLiberacao)}
              </span>
            </div>
            
            <div className="info-item">
              <label>Responsável:</label>
              <span>
                {liberacao.usuario?.nome} ({liberacao.usuario?.cargo})
              </span>
            </div>
            
            <div className="info-item">
              <label>Total de Itens:</label>
              <span style={{ fontWeight: 'bold' }}>
                {liberacao.totalItens}
              </span>
            </div>

            <div className="info-item">
              <label>Criado em:</label>
              <span>{formatarDataHora(liberacao.createdAt)}</span>
            </div>

            {liberacao.updatedAt !== liberacao.createdAt && (
              <div className="info-item">
                <label>Atualizado em:</label>
                <span>{formatarDataHora(liberacao.updatedAt)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Informações do Paciente */}
        <div className="card">
          <h3 style={{ 
            color: '#495057', 
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <User size={20} />
            Dados do Paciente
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div className="info-item">
              <label>Nome:</label>
              <span style={{ fontWeight: 'bold' }}>
                {liberacao.paciente?.nome}
              </span>
            </div>
            
            <div className="info-item">
              <label>CPF:</label>
              <span style={{ fontFamily: 'monospace' }}>
                {formatarCpf(liberacao.paciente?.cpf)}
              </span>
            </div>
            
            <div className="info-item">
              <label>RG:</label>
              <span>{liberacao.paciente?.rg || '-'}</span>
            </div>
            
            <div className="info-item">
              <label>Cartão SUS:</label>
              <span style={{ fontFamily: 'monospace' }}>
                {formatarCartaoSus(liberacao.paciente?.cartaoSus)}
              </span>
            </div>

            <div className="info-item">
              <label>Endereço:</label>
              <span>{liberacao.paciente?.endereco || '-'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Observações */}
      {liberacao.observacoes && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <h3 style={{ 
            color: '#495057', 
            marginBottom: '15px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <FileText size={20} />
            Observações da Liberação
          </h3>
          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '15px',
            borderRadius: '8px',
            border: '1px solid #e9ecef',
            whiteSpace: 'pre-wrap'
          }}>
            {liberacao.observacoes}
          </div>
        </div>
      )}

      {/* Medicamentos Liberados */}
      <div className="card">
        <h3 style={{ 
          color: '#495057', 
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <Package size={20} />
          Medicamentos Liberados
        </h3>
        
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Medicamento</th>
                <th>Princípio Ativo</th>
                <th>Fabricante</th>
                <th>Quantidade</th>
                <th>Lote</th>
                <th>Validade</th>
                <th>Observações</th>
              </tr>
            </thead>
            <tbody>
              {liberacao.itens?.map((item) => (
                <tr key={item.id}>
                  <td>
                    <div>
                      <strong>{item.nomeMedicamento}</strong>
                      <div style={{ fontSize: '12px', color: '#667' }}>
                        Tipo: {item.tipoControle.replace('_', ' ')}
                      </div>
                    </div>
                  </td>
                  <td>{item.principioAtivo || '-'}</td>
                  <td>{item.fabricante || '-'}</td>
                  <td>
                    <span style={{ 
                      fontWeight: 'bold',
                      padding: '4px 8px',
                      backgroundColor: '#e3f2fd',
                      borderRadius: '4px'
                    }}>
                      {item.quantidade}
                    </span>
                  </td>
                  <td style={{ fontFamily: 'monospace' }}>
                    {item.lote || '-'}
                  </td>
                  <td>
                    {item.dataValidade ? formatarData(item.dataValidade) : '-'}
                  </td>
                  <td>{item.observacoes || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DetalhesLiberacao;